import { Injectable } from '@angular/core';
import { CanActivate, Router, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { AppState } from 'src/app/app.states';
import { UsersSelectors } from 'src/app/features/users/state/users.selector-types';
import { FeatureFlagService } from '../services/feature-flag.service';
import {
  InvestorCapabilitiesService,
  MoneyCapabilities,
} from '../services/investor-capabilities.service';

// FIN-1709 · which capability each guarded route spends. The guard is registered on
// `/bank-accounts` and `/financial-transactions` only (app-routing.module.ts), and both
// have a flat set of children, so the paths are matched whole rather than by prefix —
// matching by prefix would open every child a row was never meant to grant.
//
// FIN-1711 · FIN-1714's row, which opened two of these for a disapproved inversionista, is
// gone — that population is refused at the login now.
const ROUTE_CAPABILITIES: { [route: string]: keyof MoneyCapabilities } = {
  '/bank-accounts': 'bankAccount',
  '/bank-accounts/create': 'bankAccount',
  '/financial-transactions/deposit': 'deposit',
  '/financial-transactions/withdraw': 'withdraw',
  '/financial-transactions/donate': 'donate',
  '/financial-transactions/invest': 'invest',
};

// The section itself opens for whoever may do any one thing inside it — the retiro screen
// is a child and cannot activate without its parent.
const SECTION_ROUTE = '/financial-transactions';

@Injectable({
  providedIn: 'root',
})
export class ProfileCompletionGuard implements CanActivate {
  constructor(
    private store: Store<AppState>,
    private router: Router,
    private featureFlagService: FeatureFlagService,
    private investorCapabilities: InvestorCapabilitiesService
  ) {}

  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> | Promise<boolean> | boolean {
    if (!this.featureFlagService.isLAFTEnabled()) {
      return new Observable(observer => {
        observer.next(true);
        observer.complete();
      });
    }

    return this.store.pipe(
      select(UsersSelectors.profile()),
      take(1),
      map(profile => {
        const capabilities = this.investorCapabilities.capabilitiesFor(profile);

        if (this.isRouteAllowed(state.url, capabilities)) {
          return true;
        }

        this.router.navigate(['/opportunities']);
        return false;
      })
    );
  }

  private isRouteAllowed(url: string, capabilities: MoneyCapabilities): boolean {
    const route = url.split(/[?#]/)[0].replace(/;[^/]*/g, '');

    if (route === SECTION_ROUTE) {
      return (
        capabilities.deposit ||
        capabilities.withdraw ||
        capabilities.donate ||
        capabilities.invest
      );
    }

    const required = ROUTE_CAPABILITIES[route];

    // A route this guard is not registered on, or a child nobody has mapped: the guard
    // has never gated it on a capability, and inventing one here would close a door
    // silently.
    return required === undefined ? true : capabilities[required];
  }
}
